import { useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { jobsApi } from '../api';
import { useJobsStore } from '../store';

const POLLING_INTERVAL = 2000; // Poll every 2 seconds when jobs are active
const IDLE_POLLING_INTERVAL = 10000; // Poll every 10 seconds when idle

interface UseJobsPollingOptions {
  enabled?: boolean;
  onJobComplete?: (jobId: string, jobType: string) => void;
}

export function useJobsPolling(options: UseJobsPollingOptions = {}) {
  const { enabled = true, onJobComplete } = options;
  const queryClient = useQueryClient();

  // Get store state and actions separately to avoid re-render loops
  const activeJobs = useJobsStore((state) => state.activeJobs);
  const pendingDownloads = useJobsStore((state) => state.pendingDownloads);
  const pendingTranslates = useJobsStore((state) => state.pendingTranslates);
  const pendingUploads = useJobsStore((state) => state.pendingUploads);
  const pendingRestarts = useJobsStore((state) => state.pendingRestarts);
  const apiDownloading = useJobsStore((state) => state.apiDownloading);
  const apiTranslating = useJobsStore((state) => state.apiTranslating);
  const apiUploading = useJobsStore((state) => state.apiUploading);
  const apiRestarting = useJobsStore((state) => state.apiRestarting);

  // Get actions as stable references
  const setActiveJobs = useJobsStore((state) => state.setActiveJobs);
  const addDownloading = useJobsStore((state) => state.addDownloading);
  const addTranslating = useJobsStore((state) => state.addTranslating);
  const addUploading = useJobsStore((state) => state.addUploading);
  const addRestarting = useJobsStore((state) => state.addRestarting);
  const isDownloading = useJobsStore((state) => state.isDownloading);
  const isTranslating = useJobsStore((state) => state.isTranslating);
  const isUploading = useJobsStore((state) => state.isUploading);
  const isRestarting = useJobsStore((state) => state.isRestarting);

  // Store both status and job_type so we have it when job disappears from active list
  const previousJobsRef = useRef<Map<string, { status: string; jobType: string }>>(new Map());
  // Store callback in ref to avoid dependency issues
  const onJobCompleteRef = useRef(onJobComplete);
  onJobCompleteRef.current = onJobComplete;

  // Determine polling interval based on whether there are active jobs
  // Also consider locally tracked cases (before polling picks them up)
  const hasActiveJobs =
    activeJobs.some((job) => job.status === 'queued' || job.status === 'running') ||
    pendingDownloads.size > 0 ||
    pendingTranslates.size > 0 ||
    pendingUploads.size > 0 ||
    pendingRestarts.size > 0 ||
    apiDownloading.size > 0 ||
    apiTranslating.size > 0 ||
    apiUploading.size > 0 ||
    apiRestarting.size > 0;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['activeJobs'],
    queryFn: () => jobsApi.getActive(),
    refetchInterval: hasActiveJobs ? POLLING_INTERVAL : IDLE_POLLING_INTERVAL,
    enabled,
    staleTime: 1000, // Consider data stale after 1 second
  });

  // Update the store when data changes
  useEffect(() => {
    if (!data?.jobs) return;

    // Build current jobs map
    const currentJobIds = new Set(data.jobs.map((j) => j.id));

    // Check for jobs that were previously active but are now gone (completed)
    for (const [jobId, prevInfo] of previousJobsRef.current) {
      const isStillActive = currentJobIds.has(jobId);

      // Job was active but is no longer in the active list = completed
      if (
        (prevInfo.status === 'queued' || prevInfo.status === 'running') &&
        !isStillActive
      ) {
        console.log(`Job ${jobId} (${prevInfo.jobType}) completed!`);
        if (onJobCompleteRef.current) {
          onJobCompleteRef.current(jobId, prevInfo.jobType);
        }
      }
    }

    // Update previous jobs reference with current jobs
    const newPreviousJobs = new Map<string, { status: string; jobType: string }>();
    for (const job of data.jobs) {
      newPreviousJobs.set(job.id, { status: job.status, jobType: job.job_type });
    }
    previousJobsRef.current = newPreviousJobs;

    // Update the store
    setActiveJobs(data.jobs);
  }, [data, setActiveJobs]);

  // Function to invalidate cases queries when jobs complete
  const invalidateCasesOnComplete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['cases'] });
    queryClient.invalidateQueries({ queryKey: ['salesforceCases'] });
  }, [queryClient]);

  // Start tracking downloads immediately and trigger a refetch
  const startDownload = useCallback(
    (caseNumbers: string[]) => {
      addDownloading(caseNumbers);
      // Refetch jobs after a short delay to pick up the new job
      setTimeout(() => refetch(), 500);
    },
    [addDownloading, refetch]
  );

  const startTranslate = useCallback(
    (caseNumbers: string[]) => {
      addTranslating(caseNumbers);
      setTimeout(() => refetch(), 500);
    },
    [addTranslating, refetch]
  );

  const startUpload = useCallback(
    (caseNumbers: string[]) => {
      addUploading(caseNumbers);
      setTimeout(() => refetch(), 500);
    },
    [addUploading, refetch]
  );

  const startRestart = useCallback(
    (caseNumbers: string[]) => {
      addRestarting(caseNumbers);
      setTimeout(() => refetch(), 500);
    },
    [addRestarting, refetch]
  );

  return {
    activeJobs,
    hasActiveJobs,
    isLoading,
    error,
    invalidateCasesOnComplete,
    refetch,
    // Functions to immediately start tracking
    startDownload,
    startTranslate,
    startUpload,
    startRestart,
    // Helper to check if a specific case is being processed (from store)
    isDownloading,
    isTranslating,
    isUploading,
    isRestarting,
  };
}
